"use client";

import type { VersionInfo } from "@/api/home";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SmartImage } from "@/components/ui/smart-image";
import { inApp } from "@/utils/interface";
import { useCallback, useMemo } from "react";

interface VersionUpdatePopupProps {
    open: boolean;
    versionInfo: VersionInfo | null;
    onOpenChange: ( open: boolean ) => void;
}

export function VersionUpdatePopup( { open, versionInfo, onOpenChange }: VersionUpdatePopupProps ) {
    const canCancel = useMemo( () => versionInfo?.levelUp === 1, [ versionInfo?.levelUp ] );
    const popupImage = versionInfo?.popBack || versionInfo?.bgUrl || "";

    const handleOpenChange = useCallback( ( newOpen: boolean ) => {
        if ( !newOpen && !canCancel ) return;
        onOpenChange( newOpen );
    }, [ canCancel, onOpenChange ] );

    const handlePreventClose = useCallback( ( e: Event ) => {
        if ( !canCancel ) {
            e.preventDefault();
        }
    }, [ canCancel ] );

    const handleUpdate = useCallback( () => {
        if ( !versionInfo?.downUrl ) return;
        if ( inApp() && typeof window.AndroidShell?.openURL === "function" ) {
            window.AndroidShell.openURL( versionInfo.downUrl );
        } else {
            window.location.href = versionInfo.downUrl;
        }

        if ( versionInfo.levelUp !== 3 ) {
            onOpenChange( false );
        }
    }, [ onOpenChange, versionInfo ] );

    if ( !versionInfo ) return null;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="w-[320px] p-0 shadow-none overflow-hidden rounded-xl"
                showCloseButton={false}
                title=""
                onInteractOutside={handlePreventClose}
                onEscapeKeyDown={handlePreventClose}
            >
                <DialogTitle className="sr-only">Version update</DialogTitle>

                <div className="bg-surface-subtle">
                    {popupImage && (
                        <SmartImage
                            src={popupImage}
                            alt="Version update"
                            width={320}
                            height={160}
                            className="w-full h-[160px] object-cover"
                        />
                    )}

                    <div className="px-4 pt-4 pb-4.5">
                        <p className="text-xl font-semibold text-t1 leading-6">Update Available</p>
                        <p className="mt-1.5 text-[13px] text-t3">Version {versionInfo.appVersion || "-"}</p>
                        <p className="mt-3 max-h-[120px] overflow-y-auto text-sm text-t3 leading-5 whitespace-pre-line break-words">
                            {versionInfo.levelContent}
                        </p>

                        <div className="mt-5 flex gap-2">
                            {canCancel && (
                                <button
                                    type="button"
                                    onClick={() => onOpenChange( false )}
                                    className="h-10 flex-1 rounded-lg border border-stroke text-sm font-medium text-t3"
                                >
                                    Later
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={handleUpdate}
                                className="h-10 flex-1 rounded-lg bg-action-accent text-content-fixed-inverse text-sm font-semibold"
                            >
                                Update Now
                            </button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
